import React, { Component } from 'react';
import {
    Button,
    ButtonVariants,
    ButtonColors,
    Modal,
    Text,
} from '@clockout-dev/design-elements';
import ReportsExportMenu from './elements/ReportsExportMenu';
import ReportsController from './reports.controller';
import withHooks from '../../core/withHooks';
import Inherit from '../../core/inherit';
import Page from '../common/page/Page';
import { SCOPES } from '../../constants/scopes.constants';
import SplitLayout from '../common/layouts/splitLayout/SplitLayout';
import LedgerTable from '../common/ledgerTable/LedgerTable';
import { LEDGER_ROW_ACTION } from '../common/ledgerTable/ledgerTable.constants';
import ReportsUsersTable from './elements/ReportsUsersTable';
import ReportsFilterBar from './elements/ReportsFilterBar';
import ReportsFiltersFields from './elements/ReportsFiltersFields';
import ReportsTransactionsFilterFields from './elements/ReportsTransactionsFilterFields';
import ReportsActiveFilters from './elements/ReportsActiveFilters';
import ReportsFunnelMetrics from './elements/ReportsFunnelMetrics';
import UserDetailPanel from './elements/UserDetailPanel';
import ReportsUsersNotice from './elements/ReportsUsersNotice';
import { SYNC_STATE_CATALOG_WARNING } from '../common/statusClassification/statusClassification';
import { UserActionConfirmModal } from '../common/userActions';
import styles from './styles/reports.module.scss';
import tableStyles from './elements/styles/reports-table.module.scss';
import {
    getReportsTabs,
    DEFAULT_FILTERS,
    DEFAULT_TRANSACTION_FILTERS,
    DEFAULT_PAGE_SIZE,
    USERS_FILTER_MODAL_COPY,
    TRANSACTIONS_FILTER_MODAL_COPY,
    FUNNEL_FILTER_COPY,
    USERS_TABLE_COPY,
} from './reports.constants';
import { FUNNEL_SKELETON } from './funnelPayload';

class Reports extends Inherit(ReportsController, Component) {
    state = {
        user: {},
        loading: true,
        usersLoading: true,
        transactionsLoading: true,
        users: [],
        usersRows: [],
        usersPageInfo: { current: 1, perPage: DEFAULT_PAGE_SIZE, total: 0 },
        transactionsRows: [],
        transactionsPageInfo: { current: 1, perPage: DEFAULT_PAGE_SIZE, total: 0 },
        activeTab: 'users',
        // Users tab filter model. The Transactions tab has its own below; the
        // two never share a key so a filter cannot leak between tabs.
        filters: { ...DEFAULT_FILTERS },
        transactionFilters: { ...DEFAULT_TRANSACTION_FILTERS },
        // Controlled sort for the transactions table (server-side). Null means
        // the user has not sorted — the endpoint still returns rows
        // newest-first, but no header claims to be the reason.
        transactionsSort: null,
        // The zone the ledger endpoint evaluated its date range in; the table
        // renders every timestamp in it and names it in the date headings.
        transactionsTimezone: null,
        // A failed fetch is not an empty result. Kept apart so the table's
        // empty state can say which of the two it is.
        transactionsFailed: false,
        // Per-user sync detail (`idle | loading | ready | error`) for the side
        // panel only — the table reads the bulk rollup off each row.
        syncDetailByClientId: {},
        usersSort: null,        // { by, dir } | null — mirrored to ?sortBy/?sortOrder
        usersWarnings: [],      // meta.warnings codes from the users payload
        ssoLoading: {},
        restrictLoading: {},
        deleteSyncLoading: {},
        markCompleteLoading: {},
        restrictedClientIds: {},
        // Maintained by `ResponsiveController` (mixed into
        // `ReportsController` via `Inherit`). Declared here because
        // component field declarations run after the mixin's constructor
        // and would therefore overwrite any initial value from the mixin.
        // `isSmallScreen` (mobile || tablet) is derived via a getter on
        // the controller — see `ReportsController.isSmallScreen`.
        isMobile: false,
        isTablet: false,
        selectedUserId: null,
        // When set ({ clientId, boot }), the side panel swaps its detail
        // sections for the embedded consumer app booted for that user.
        ssoEmbed: null,
        funnel: null,
        // `true` from the first paint (like usersLoading) so the skeleton grid
        // renders in the same tick as the page instead of popping in after
        // initialize() flips it — no layout shift on mount.
        funnelLoading: true,
        funnelError: null,      // { reason: FUNNEL_ERROR.LOAD_FAILED | FUNNEL_ERROR.EMPTY } | null
        funnelRange: null,      // { type, label, generatedAt } | null
        funnelOptedIn: null,    // { count, definition } | null
        // Server-side export (reports-export.controller.js): `exporting` while
        // the request is in flight; `exportJob` while an async job is polled.
        exporting: false,
        exportJob: null,
        // Card-vs-table discrepancy for the active card, if any (KAN-583).
        funnelMismatch: null,
        // Last users request failed (transport error or a refused card filter);
        // the table shows failed copy, never the empty state. `usersFunnelRange`
        // is the window the service used for the card population.
        usersFailed: false,
        usersFunnelRange: null,
        usersSearch: '',
        transactionsSearch: '',
        // Bumped by each tab's reset so the (uncontrolled) search box remounts
        // empty — see `resetTransactionFilters` / `resetFilters`.
        usersSearchResetKey: 0,
        transactionsSearchResetKey: 0,
        // Side-panel drawer state on tablet/mobile. On desktop the panel
        // is rendered inline in the grid; on screens < 1024px it opens as
        // a full-screen modal when a row is selected.
        panelOpenMobile: false,
    };

    // `getSelectedUser`, `handleSelectUser` and `handleClosePanel` live on
    // `ReportsController`. They are inherited through `Inherit(...)` and
    // bound where they are passed as React callbacks (see usages below).

    // ---------- renderers ---------------------------------------------------
    renderUserDetailPanel({ asModal = false } = {}) {
        const {
            syncDetailByClientId,
            ssoLoading,
            restrictLoading,
            selectedUserId,
            ssoEmbed,
        } = this.state;
        const selectedUser = this.getSelectedUser();
        const ssoBoot = ssoEmbed && ssoEmbed.clientId === selectedUserId ? ssoEmbed.boot : null;

        return (
            <UserDetailPanel
                user={selectedUser}
                syncDetail={selectedUserId ? syncDetailByClientId?.[selectedUserId] || null : null}
                onRetrySync={(clientId) => this.loadSyncDetailForUser(clientId, { force: true, patchRow: true })}
                ssoBoot={ssoBoot}
                onOpenSso={this.openSsoForUser.bind(this)}
                onExitSso={this.exitSsoForUser.bind(this)}
                onViewUser={(clientId) => this.navigateUser?.(clientId)}
                onRestrictUser={this.restrictUser.bind(this)}
                ssoLoading={selectedUserId ? !!ssoLoading?.[selectedUserId] : false}
                restrictLoading={selectedUserId ? !!restrictLoading?.[selectedUserId] : false}
                isRestricted={this.isUserRestricted(selectedUserId, selectedUser)}
                asModal={asModal}
                canRestrict={this.canRestrictUser}
                canOpenSso={this.canOpenSso}
                canViewUser={this.canViewUser}
            />
        );
    }

    // The two `renderFields` are class fields, built once per instance rather
    // than as a fresh closure on every render; they read state when called.
    renderUsersFilterFields = ({ isMobile }) => (
        <ReportsFiltersFields
            isMobile={isMobile}
            filters={this.state.filters}
            handleFilterChange={(name, value) =>
                this.handleFilterChange(name, value, { apply: !isMobile })
            }
            handleTimeFrameChange={(value) =>
                this.handleTimeFrameChange(value, { apply: !isMobile })
            }
        />
    );

    renderTransactionsFilterFields = ({ isMobile }) => {
        // Desktop chips apply on change; inside the small-screen modal a change
        // only stages, and the modal's Filter button applies. The delegate gets
        // the fetch as a callback so it never has to know about it.
        const apply = isMobile ? null : (filters) => this.applyTransactionFilters(filters);
        return (
            <ReportsTransactionsFilterFields
                isMobile={isMobile}
                filters={this.state.transactionFilters}
                handleFilterChange={(name, value) => this.handleTransactionFilterChange(name, value, { apply })}
                handleTimeFrameChange={(value) => this.handleTransactionTimeFrameChange(value, { apply })}
                handleDateRangeChange={(range) => this.handleTransactionDateRangeChange(range, { apply })}
            />
        );
    };

    renderUsersFilterBar() {
        const { usersSearchResetKey } = this.state;
        return (
            <ReportsFilterBar
                tabKey="users"
                searchResetKey={usersSearchResetKey}
                isSmallScreen={this.isSmallScreen}
                onSearch={this.handleUsersSearch.bind(this)}
                applyFilters={this.applyFilters.bind(this)}
                resetFilters={this.resetFilters.bind(this)}
                modalCopy={USERS_FILTER_MODAL_COPY}
                renderFields={this.renderUsersFilterFields}
            />
        );
    }

    renderTransactionsFilterBar() {
        const { transactionsSearchResetKey } = this.state;
        return (
            <ReportsFilterBar
                tabKey="transactions"
                searchResetKey={transactionsSearchResetKey}
                isSmallScreen={this.isSmallScreen}
                onSearch={this.handleTransactionsSearch.bind(this)}
                applyFilters={this.applyTransactionFilters.bind(this)}
                resetFilters={this.resetTransactionFilters.bind(this)}
                modalCopy={TRANSACTIONS_FILTER_MODAL_COPY}
                renderFields={this.renderTransactionsFilterFields}
            />
        );
    }

    renderTransactionsTab() {
        const {
            transactionsRows,
            transactionsLoading,
            transactionsPageInfo,
            transactionsSort,
            transactionFilters,
            transactionsTimezone,
            transactionsFailed,
        } = this.state;

        return (
            <div className={styles.transactionsArea}>
                {this.renderTransactionsFilterBar()}
                <ReportsActiveFilters
                    chips={this.getActiveTransactionFilterChips()}
                    onClear={this.resetTransactionFilters.bind(this)}
                />
                <LedgerTable
                    rows={transactionsRows}
                    loading={transactionsLoading}
                    pageInfo={transactionsPageInfo}
                    onChange={this.handleTransactionsTableChange.bind(this)}
                    sort={transactionsSort}
                    typeFilter={transactionFilters?.type ?? null}
                    // The filter bar owns `type` on this tab; the column keeps its sort.
                    showTypeFilter={false}
                    showFees
                    showUserColumn
                    onViewUser={(clientId) => this.navigateUser?.(clientId)}
                    canViewUser={this.canViewUser}
                    canAction={this.canViewUser}
                    rowAction={LEDGER_ROW_ACTION.VIEW_USER}
                    onRowAction={(record) => this.navigateUser?.(record?.client_id)}
                    timezone={transactionsTimezone}
                    hasActiveFilters={this.hasActiveTransactionFilters()}
                    onClearFilters={this.resetTransactionFilters.bind(this)}
                    failed={transactionsFailed}
                    // Re-reads page, sort, filters and search from state, so the
                    // retry lands on exactly the slice the failed request asked for.
                    onRetry={() => this.loadTransactions()}
                    classNames={{ shell: tableStyles.tableShell, root: tableStyles.tableRoot }}
                />
            </div>
        );
    }

    render() {
        const {
            activeTab,
            filters,
            funnel,
            funnelLoading,
            funnelError,
            funnelRange,
            funnelOptedIn,
            funnelMismatch,
            panelOpenMobile,
            usersLoading,
            usersFailed,
            usersRows,
            usersPageInfo,
            usersSort,
            usersWarnings,
            syncDetailByClientId,
            restrictLoading,
            deleteSyncLoading,
            markCompleteLoading,
            selectedUserId,
            exporting,
            exportJob,
        } = this.state;
        const noticeWarnings = [
            ...(usersWarnings || []),
            ...(this.state.syncStateCatalogFailed ? [SYNC_STATE_CATALOG_WARNING] : []),
        ];

        const selectedUser = this.getSelectedUser();
        const isUsers = activeTab === 'users';
        const isSmallScreen = this.isSmallScreen;

        const reportsTabs = getReportsTabs(this.hasScope(SCOPES.REPORTS_TRANSACTIONS_VIEW));

        return (
            <Page
                title={{
                    text: 'User & Transaction',
                    highlight: 'Data',
                }}
                scope={SCOPES.REPORTS_VIEW}
                description={isUsers ? 'View and manage user data' : 'View and manage transaction data'}
                tabs={reportsTabs}
                onTabChange={(key) => this.handleTabChange(key)}
                notificationContextHolder={this.props.notificationContextHolder}
                action={
                    <div className={styles.headerActions}>
                        <ReportsExportMenu
                            onSelect={this.requestExport.bind(this)}
                            byDateDisabled={!this.hasActiveDateRange()}
                            loading={exporting}
                            job={exportJob?.tab === activeTab ? exportJob : null}
                        />
                        <Button
                            variant={ButtonVariants.PRIMARY}
                            color={ButtonColors.SECONDARY}
                            onClick={() => this.refreshData()}>
                            <Text size="xs" fontWeight="semibold" className={styles.headerActionsText}>Refresh Data</Text>
                        </Button>
                    </div>
                }>
                <div className={styles.contentWrapper}>
                    {isUsers ? (
                        <ReportsFunnelMetrics
                            metrics={funnel}
                            loading={funnelLoading}
                            error={funnelError}
                            range={funnelRange}
                            optedIn={funnelOptedIn}
                            onRetry={() => this.loadFunnelMetrics()}
                            placeholders={FUNNEL_SKELETON}
                            activeStage={filters.funnelStage || null}
                            onStageClick={this.handleFunnelStageClick.bind(this)}
                            mismatch={funnelMismatch}
                        />
                    ) : null}
                    {isUsers ? (
                        <SplitLayout
                            key="reports-users"
                            className={styles.splitRoot}
                            contentClassName={styles.splitContent}
                            sideClassName={styles.splitSide}
                            side={!isSmallScreen ? this.renderUserDetailPanel() : null}
                        >
                            {this.renderUsersFilterBar()}
                            <ReportsActiveFilters
                                chips={this.getActiveUsersFilterChips()}
                                onClear={this.handleFunnelStageClear.bind(this)}
                            />
                            {noticeWarnings.length ? (
                                <ReportsUsersNotice warnings={noticeWarnings} onRetry={() => this.refreshData()} />
                            ) : null}
                            <ReportsUsersTable
                                rows={usersRows}
                                loading={usersLoading}
                                pageInfo={usersPageInfo}
                                emptyDescription={
                                    usersFailed
                                        ? USERS_TABLE_COPY.FAILED
                                        : (filters.funnelStage ? FUNNEL_FILTER_COPY.EMPTY_STATE : USERS_TABLE_COPY.EMPTY)
                                }
                                sort={usersSort}
                                onTableChange={this.handleUsersTableChange.bind(this)}
                                onSelectUser={this.handleSelectUser.bind(this)}
                                onViewUser={(clientId) => this.navigateUser?.(clientId)}
                                canViewUser={this.canViewUser}
                                selectedUserId={selectedUserId}
                                onRestrictUser={this.restrictUser.bind(this)}
                                onDeleteLatestSync={this.deleteLatestSync.bind(this)}
                                onMarkLatestSyncComplete={this.markLatestSyncComplete.bind(this)}
                                syncDetailByClientId={syncDetailByClientId}
                                onRetrySyncStatus={() => this.refreshData()}
                                restrictLoading={restrictLoading}
                                deleteSyncLoading={deleteSyncLoading}
                                markCompleteLoading={markCompleteLoading}
                                isRestricted={this.isUserRestricted.bind(this)}
                                canRestrict={this.canRestrictUser}
                                canDeleteSync={this.canDeleteSync}
                                canMarkSyncComplete={this.canMarkSyncComplete}
                            />
                        </SplitLayout>
                    ) : this.renderTransactionsTab()}
                </div>

                {/* Mobile / tablet: the panel is shown as a full-screen
                    modal when a row is selected. The `selectedUser` guard
                    skips the empty-state on mobile (better to hide the
                    drawer entirely). */}
                {isUsers ? (
                    <Modal
                        open={panelOpenMobile && !!selectedUser}
                        onCancel={this.handleClosePanel.bind(this)}
                        footer={null}
                        title={null}
                        className={styles.panelModalOverride}
                        destroyOnHidden
                        width="100%"
                    >
                        {this.renderUserDetailPanel({ asModal: true })}
                    </Modal>
                ) : null}

                {/* One confirmation for both member actions, rendered at page
                    level: a modal is a singleton no matter how many rows the
                    table has, and the target lives inside the confirm object. */}
                <UserActionConfirmModal
                    confirm={this.userActionConfirm}
                    onCancel={() => this.cancelUserAction()}
                    onConfirm={() => this.userActionConfirmSubmit()}
                />
            </Page>
        );
    }
}

export default withHooks(Reports);
